import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v3API } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  FileText, Download, Plus, Trash2, Save, ArrowLeft, Upload, RefreshCw,
  Search, Loader2, Camera, X, ChevronUp, ChevronDown, Wand2, Award,
  ArrowLeftRight, Eye, EyeOff, Palette, User, Mail, Briefcase, Edit3,
  Sparkles, ClipboardPaste, Target, Zap, LayoutList, History, RotateCcw
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const SIDEBAR_COLORS = [
  { id: 'navy', name: 'Navy Blue', value: '#1e3a5f' },
  { id: 'charcoal', name: 'Charcoal', value: '#374151' },
  { id: 'indigo', name: 'Indigo', value: '#4f46e5' },
  { id: 'teal', name: 'Teal', value: '#0d9488' },
  { id: 'burgundy', name: 'Burgundy', value: '#7f1d1d' },
  { id: 'forest', name: 'Forest Green', value: '#166534' }
];

const MOVABLE_SECTIONS = [
  { id: 'skills', label: 'Skills' },
  { id: 'languages', label: 'Languages' },
  { id: 'certifications', label: 'Certifications' },
  { id: 'education', label: 'Education' },
  { id: 'experience', label: 'Experience' }
];

export default function CVBuilderV3() {
  const navigate = useNavigate();
  const { cvId } = useParams();
  
  // View state
  const [view, setView] = useState(cvId ? 'edit' : 'list'); // 'list', 'create', 'edit'
  const [templates, setTemplates] = useState([]);
  const [cvList, setCvList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Create form state
  const [candidateName, setCandidateName] = useState('');
  const [rawText, setRawText] = useState('');
  const [language, setLanguage] = useState('en');
  const [selectedTemplate, setSelectedTemplate] = useState('v3-executive-sidebar');
  const [atsOptimize, setAtsOptimize] = useState(true);
  const [targetRole, setTargetRole] = useState('');
  
  // Edit state
  const [currentCv, setCurrentCv] = useState(null);
  const [structuredCv, setStructuredCv] = useState(null);
  const [sectionVisibility, setSectionVisibility] = useState({});
  const [sectionLayout, setSectionLayout] = useState({ sidebar: [], main: [] });
  const [showPhoto, setShowPhoto] = useState(true);
  const [sidebarColor, setSidebarColor] = useState('#1e3a5f');
  const [previewHtml, setPreviewHtml] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [generatingTasksFor, setGeneratingTasksFor] = useState(null);
  
  // Smart Edit state
  const [smartEditContent, setSmartEditContent] = useState('');
  const [smartEditMode, setSmartEditMode] = useState('conservative');
  const [smartEditLoading, setSmartEditLoading] = useState(false);
  const [showSmartEdit, setShowSmartEdit] = useState(false);
  
  // Version History state
  const [versions, setVersions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Cover Letter state
  const [activeTab, setActiveTab] = useState('cv'); // 'cv' or 'coverLetter'
  const [coverLetters, setCoverLetters] = useState([]);
  const [currentCoverLetter, setCurrentCoverLetter] = useState(null);
  const [coverLetterType, setCoverLetterType] = useState('general'); // 'general' or 'specific'
  const [coverLetterJobTitle, setCoverLetterJobTitle] = useState('');
  const [coverLetterCompany, setCoverLetterCompany] = useState('');
  const [coverLetterHiringManager, setCoverLetterHiringManager] = useState('Hiring Manager');
  const [coverLetterContent, setCoverLetterContent] = useState('');
  const [generatingCoverLetter, setGeneratingCoverLetter] = useState(false);
  const [coverLetterPreviewHtml, setCoverLetterPreviewHtml] = useState('');

  // Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const data = await v3API.getTemplates();
        setTemplates(data.templates || []);
      } catch (error) {
        console.error('Failed to load templates:', error);
      }
    };
    loadTemplates();
  }, []);

  // Load CV list
  const loadCvList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await v3API.getCVs(searchQuery);
      setCvList(data.cvs || []);
    } catch (error) {
      console.error('Failed to load CVs:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (view === 'list') {
      loadCvList();
    }
  }, [view, loadCvList]);

  // Load specific CV
  useEffect(() => {
    const loadCv = async () => {
      if (!cvId) return;
      setLoading(true);
      try {
        const cv = await v3API.getCV(cvId);
        setCurrentCv(cv);
        setStructuredCv(cv.structuredCv || {});
        setSectionVisibility(cv.sectionVisibility || {});
        setSectionLayout(cv.sectionLayout || { sidebar: [], main: [] });
        setShowPhoto(cv.showPhoto !== false);
        setSidebarColor(cv.sidebarColor || '#1e3a5f');
        setSelectedTemplate(cv.v3TemplateId || 'v3-executive-sidebar');
        setPreviewHtml(cv.htmlSnapshot || '');
        setView('edit');
      } catch (error) {
        toast.error('Failed to load CV');
        navigate('/cv-builder-v3');
      } finally {
        setLoading(false);
      }
    };
    loadCv();
  }, [cvId, navigate]);

  // Load versions when history panel is opened
  useEffect(() => {
    if (showHistory && currentCv?.id) {
      loadVersions(currentCv.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHistory, currentCv?.id]);

  // Handle popstate for back button
  useEffect(() => {
    const handlePopState = () => {
      if (!cvId && view === 'edit') {
        setView('list');
        setCurrentCv(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [cvId, view]);

  // Create CV
  const handleCreate = async () => {
    if (!candidateName.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (!rawText.trim()) {
      toast.error('Please paste your CV content');
      return;
    }
    
    setGenerating(true);
    try {
      const templateInfo = templates.find(t => t.id === selectedTemplate);
      const cv = await v3API.createCV({
        candidateName,
        rawText,
        language,
        v3TemplateId: selectedTemplate,
        showPhoto: true,
        sidebarColor: templateInfo?.category === 'sidebar' ? sidebarColor : '#1e3a5f',
        atsOptimize,
        targetRole: targetRole.trim()
      });
      
      toast.success(atsOptimize ? 'Professional CV created!' : 'CV created successfully!');
      navigate(`/cv-builder-v3/${cv.id}`);
    } catch (error) {
      toast.error('Failed to create CV');
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  // Save CV
  const saveCV = async (overrideData = {}) => {
    if (!currentCv?.id) return;
    
    setSaving(true);
    try {
      const updated = await v3API.updateCV(currentCv.id, {
        v3TemplateId: overrideData.v3TemplateId || selectedTemplate,
        structuredCv: overrideData.structuredCv || structuredCv,
        showPhoto: overrideData.showPhoto !== undefined ? overrideData.showPhoto : showPhoto,
        sidebarColor: overrideData.sidebarColor || sidebarColor,
        sectionLayout: overrideData.sectionLayout || sectionLayout
      });
      setCurrentCv(updated);
      setPreviewHtml(updated.htmlSnapshot || '');
      if (!overrideData.silent) {
        toast.success('CV saved!');
      }
    } catch (error) {
      toast.error('Failed to save CV');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Auto-save when template changes
  const handleTemplateChange = async (newTemplate) => {
    setSelectedTemplate(newTemplate);
    if (currentCv?.id) {
      await saveCV({ v3TemplateId: newTemplate, silent: true });
    }
  };

  // Auto-save when sidebar color changes
  const handleSidebarColorChange = async (newColor) => {
    setSidebarColor(newColor);
    if (currentCv?.id) {
      await saveCV({ sidebarColor: newColor, silent: true });
    }
  };

  // Auto-save when photo toggle changes
  const handlePhotoToggle = async (newValue) => {
    setShowPhoto(newValue);
    if (currentCv?.id) {
      await saveCV({ showPhoto: newValue, silent: true });
    }
  };

  // Refresh preview
  const refreshPreview = async () => {
    if (!currentCv?.id) return;
    try {
      const cv = await v3API.getCV(currentCv.id);
      setPreviewHtml(cv.htmlSnapshot || '');
    } catch (error) {
      console.error('Failed to refresh preview:', error);
    }
  };

  // Smart Edit handler
  const handleSmartEdit = async () => {
    if (!smartEditContent.trim()) {
      toast.error('Please paste some content first');
      return;
    }
    
    setSmartEditLoading(true);
    try {
      const result = await v3API.smartEdit(currentCv.id, {
        pastedContent: smartEditContent,
        conservativeMode: smartEditMode === 'conservative',
        replaceMode: smartEditMode === 'replace'
      });
      
      if (result.success) {
        setStructuredCv(result.structuredCv);
        setPreviewHtml(result.htmlSnapshot);
        setSmartEditContent('');
        setShowSmartEdit(false);
        
        const changeCount = result.changes?.length || 0;
        if (changeCount > 0) {
          toast.success(`Smart Edit complete! ${changeCount} change${changeCount !== 1 ? 's' : ''} made`);
        } else {
          toast.info('No new content to add (may already exist)');
        }
      }
    } catch (error) {
      console.error('Smart Edit error:', error);
      toast.error(error.response?.data?.detail || 'Smart Edit failed');
    } finally {
      setSmartEditLoading(false);
    }
  };

  // Version History handlers
  const loadVersions = useCallback(async (cvIdToLoad) => {
    const id = cvIdToLoad || currentCv?.id;
    if (!id) return;
    setHistoryLoading(true);
    try {
      const data = await v3API.getVersions(id);
      setVersions(data.versions || []);
    } catch (error) {
      console.error('Failed to load versions:', error);
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  }, [currentCv]);

  const handleRestoreVersion = async (versionId, versionNumber) => {
    if (!currentCv?.id) return;
    if (!window.confirm(`Restore CV to version ${versionNumber}? Your current state will be saved as a new version.`)) {
      return;
    }
    try {
      const restored = await v3API.restoreVersion(currentCv.id, versionId);
      setCurrentCv(restored);
      setStructuredCv(restored.structuredCv || {});
      setSectionVisibility(restored.sectionVisibility || {});
      setSectionLayout(restored.sectionLayout || { sidebar: [], main: [] });
      setShowPhoto(restored.showPhoto !== false);
      setSidebarColor(restored.sidebarColor || '#1e3a5f');
      setPreviewHtml(restored.htmlSnapshot || '');
      await loadVersions(currentCv.id);
      toast.success(`Restored to version ${versionNumber}`);
    } catch (error) {
      console.error('Restore failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore version');
    }
  };

  const handleDeleteVersion = async (versionId, versionNumber) => {
    if (!currentCv?.id) return;
    if (!window.confirm(`Delete version ${versionNumber}? This cannot be undone.`)) return;
    try {
      await v3API.deleteVersion(currentCv.id, versionId);
      await loadVersions(currentCv.id);
      toast.success(`Version ${versionNumber} deleted`);
    } catch (error) {
      console.error('Delete version failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete version');
    }
  };

  // Update field helper
  const updateField = (path, value) => {
    setStructuredCv(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.');
      let obj = updated;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!obj[parts[i]]) obj[parts[i]] = {};
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return updated;
    });
  };

  // Toggle section visibility
  const toggleSectionVisibility = async (section) => {
    const newVisibility = { ...sectionVisibility, [section]: !sectionVisibility[section] };
    setSectionVisibility(newVisibility);
    if (currentCv?.id) {
      try {
        await v3API.updateVisibility(currentCv.id, newVisibility);
        refreshPreview();
      } catch (error) {
        console.error('Failed to update visibility:', error);
      }
    }
  };

  // Move section between sidebar and main
  const moveSection = async (sectionId, targetArea) => {
    const newLayout = { ...sectionLayout };
    
    // Remove from current location
    newLayout.sidebar = newLayout.sidebar.filter(s => s !== sectionId);
    newLayout.main = newLayout.main.filter(s => s !== sectionId);
    
    // Add to target
    if (targetArea === 'sidebar') {
      newLayout.sidebar = [...newLayout.sidebar, sectionId];
    } else {
      newLayout.main = [...newLayout.main, sectionId];
    }
    
    setSectionLayout(newLayout);
    
    if (currentCv?.id) {
      try {
        await v3API.updateSectionLayout(currentCv.id, newLayout);
        refreshPreview();
      } catch (error) {
        console.error('Failed to update layout:', error);
      }
    }
  };

  // Experience functions
  const addExperience = () => {
    setStructuredCv(prev => ({
      ...prev,
      experience: [...(prev.experience || []), {
        role: '', company: '', start: '', end: '', bullets: []
      }]
    }));
  };

  const removeExperience = (index) => {
    setStructuredCv(prev => ({
      ...prev,
      experience: prev.experience.filter((_, i) => i !== index)
    }));
  };

  const moveExperienceUp = (index) => {
    if (index === 0) return;
    setStructuredCv(prev => {
      const exp = [...prev.experience];
      [exp[index - 1], exp[index]] = [exp[index], exp[index - 1]];
      return { ...prev, experience: exp };
    });
  };

  const moveExperienceDown = (index) => {
    setStructuredCv(prev => {
      if (index >= prev.experience.length - 1) return prev;
      const exp = [...prev.experience];
      [exp[index], exp[index + 1]] = [exp[index + 1], exp[index]];
      return { ...prev, experience: exp };
    });
  };

  // Education functions
  const addEducation = () => {
    setStructuredCv(prev => ({
      ...prev,
      education: [...(prev.education || []), {
        degree: '', school: '', start: '', end: ''
      }]
    }));
  };

  const removeEducation = (index) => {
    setStructuredCv(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
  };

  const moveEducationUp = (index) => {
    if (index === 0) return;
    setStructuredCv(prev => {
      const edu = [...prev.education];
      [edu[index - 1], edu[index]] = [edu[index], edu[index - 1]];
      return { ...prev, education: edu };
    });
  };

  const moveEducationDown = (index) => {
    setStructuredCv(prev => {
      if (index >= prev.education.length - 1) return prev;
      const edu = [...prev.education];
      [edu[index], edu[index + 1]] = [edu[index + 1], edu[index]];
      return { ...prev, education: edu };
    });
  };

  // Bullet functions
  const addBullet = (expIndex) => {
    setStructuredCv(prev => {
      const updated = { ...prev };
      updated.experience = [...updated.experience];
      updated.experience[expIndex] = {
        ...updated.experience[expIndex],
        bullets: [...(updated.experience[expIndex].bullets || []), '']
      };
      return updated;
    });
  };

  const updateBullet = (expIndex, bulletIndex, value) => {
    setStructuredCv(prev => {
      const updated = { ...prev };
      updated.experience = [...updated.experience];
      updated.experience[expIndex] = { ...updated.experience[expIndex] };
      updated.experience[expIndex].bullets = [...updated.experience[expIndex].bullets];
      updated.experience[expIndex].bullets[bulletIndex] = value;
      return updated;
    });
  };

  const removeBullet = (expIndex, bulletIndex) => {
    setStructuredCv(prev => {
      const updated = { ...prev };
      updated.experience = [...updated.experience];
      updated.experience[expIndex] = { ...updated.experience[expIndex] };
      updated.experience[expIndex].bullets = updated.experience[expIndex].bullets.filter((_, i) => i !== bulletIndex);
      return updated;
    });
  };

  // AI Generate Tasks
  const generateTasks = async (expIndex) => {
    const exp = structuredCv.experience[expIndex];
    if (!exp.role) {
      toast.error('Please enter a job title first');
      return;
    }
    
    setGeneratingTasksFor(expIndex);
    try {
      const result = await v3API.generateTasks(exp.role, exp.company);
      if (result.success && result.tasks) {
        setStructuredCv(prev => {
          const updated = { ...prev };
          updated.experience = [...updated.experience];
          updated.experience[expIndex] = {
            ...updated.experience[expIndex],
            bullets: [...(updated.experience[expIndex].bullets || []), ...result.tasks]
          };
          return updated;
        });
        toast.success(`Generated ${result.tasks.length} tasks!`);
      }
    } catch (error) {
      toast.error('Failed to generate tasks');
    } finally {
      setGeneratingTasksFor(null);
    }
  };

  // Certification functions
  const addCertification = () => {
    setStructuredCv(prev => ({
      ...prev,
      certifications: [...(prev.certifications || []), { name: '', issuer: '', year: '' }]
    }));
  };

  const removeCertification = (index) => {
    setStructuredCv(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  // Custom Sections
  const addCustomSection = () => {
    setStructuredCv(prev => ({
      ...prev,
      customSections: [...(prev.customSections || []), { title: 'New Section', items: [''] }]
    }));
  };

  const removeCustomSection = (index) => {
    setStructuredCv(prev => ({
      ...prev,
      customSections: prev.customSections.filter((_, i) => i !== index)
    }));
  };

  const updateCustomSectionTitle = (index, title) => {
    setStructuredCv(prev => {
      const sections = [...(prev.customSections || [])];
      sections[index] = { ...sections[index], title };
      return { ...prev, customSections: sections };
    });
  };

  const addCustomSectionItem = (sectionIndex) => {
    setStructuredCv(prev => {
      const sections = [...(prev.customSections || [])];
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        items: [...(sections[sectionIndex].items || []), '']
      };
      return { ...prev, customSections: sections };
    });
  };

  const removeCustomSectionItem = (sectionIndex, itemIndex) => {
    setStructuredCv(prev => {
      const sections = [...(prev.customSections || [])];
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        items: sections[sectionIndex].items.filter((_, i) => i !== itemIndex)
      };
      return { ...prev, customSections: sections };
    });
  };

  const updateCustomSectionItem = (sectionIndex, itemIndex, value) => {
    setStructuredCv(prev => {
      const sections = [...(prev.customSections || [])];
      const items = [...sections[sectionIndex].items];
      items[itemIndex] = value;
      sections[sectionIndex] = { ...sections[sectionIndex], items };
      return { ...prev, customSections: sections };
    });
  };

  // Photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentCv?.id) return;
    
    setPhotoUploading(true);
    try {
      await v3API.uploadPhoto(currentCv.id, file);
      const cv = await v3API.getCV(currentCv.id);
      setCurrentCv(cv);
      setStructuredCv(cv.structuredCv);
      setPreviewHtml(cv.htmlSnapshot);
      toast.success('Photo uploaded!');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  // Delete CV
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this CV?')) return;
    try {
      await v3API.deleteCV(id);
      toast.success('CV deleted');
      loadCvList();
    } catch (error) {
      toast.error('Failed to delete CV');
    }
  };

  // ============ COVER LETTER FUNCTIONS ============
  
  // Load cover letters for current CV
  const loadCoverLetters = async () => {
    if (!currentCv?.id) return;
    try {
      const data = await v3API.getCoverLetters(currentCv.id);
      setCoverLetters(data.coverLetters || []);
    } catch (error) {
      console.error('Failed to load cover letters:', error);
    }
  };

  // Generate cover letter
  const generateCoverLetter = async () => {
    if (!currentCv?.id) return;
    
    if (coverLetterType === 'specific' && !coverLetterJobTitle.trim()) {
      toast.error('Please enter a job title for a specific cover letter');
      return;
    }
    
    setGeneratingCoverLetter(true);
    try {
      const result = await v3API.generateCoverLetter({
        cvId: currentCv.id,
        letterType: coverLetterType,
        jobTitle: coverLetterJobTitle,
        companyName: coverLetterCompany,
        hiringManager: coverLetterHiringManager || 'Hiring Manager'
      });
      
      setCurrentCoverLetter(result);
      setCoverLetterContent(result.content);
      setCoverLetterPreviewHtml(result.htmlSnapshot);
      setCoverLetters(prev => [result, ...prev]);
      toast.success('Cover letter generated!');
    } catch (error) {
      toast.error('Failed to generate cover letter');
      console.error(error);
    } finally {
      setGeneratingCoverLetter(false);
    }
  };

  // Update cover letter
  const updateCoverLetter = async () => {
    if (!currentCoverLetter?.id) return;
    
    try {
      const result = await v3API.updateCoverLetter(currentCoverLetter.id, {
        content: coverLetterContent,
        hiringManager: coverLetterHiringManager,
        jobTitle: coverLetterJobTitle,
        companyName: coverLetterCompany
      });
      
      setCurrentCoverLetter(result);
      setCoverLetterPreviewHtml(result.htmlSnapshot);
      setCoverLetters(prev => prev.map(l => l.id === result.id ? result : l));
      toast.success('Cover letter saved!');
    } catch (error) {
      toast.error('Failed to update cover letter');
    }
  };

  // Select a cover letter
  const selectCoverLetter = async (letter) => {
    setCurrentCoverLetter(letter);
    setCoverLetterContent(letter.content);
    setCoverLetterJobTitle(letter.jobTitle || '');
    setCoverLetterCompany(letter.companyName || '');
    setCoverLetterHiringManager(letter.hiringManager || 'Hiring Manager');
    setCoverLetterType(letter.letterType || 'general');
    setCoverLetterPreviewHtml(letter.htmlSnapshot);
  };

  // Delete cover letter
  const deleteCoverLetter = async (letterId) => {
    if (!window.confirm('Delete this cover letter?')) return;
    try {
      await v3API.deleteCoverLetter(letterId);
      setCoverLetters(prev => prev.filter(l => l.id !== letterId));
      if (currentCoverLetter?.id === letterId) {
        setCurrentCoverLetter(null);
        setCoverLetterContent('');
        setCoverLetterPreviewHtml('');
      }
      toast.success('Cover letter deleted');
    } catch (error) {
      toast.error('Failed to delete cover letter');
    }
  };

  // Load cover letters when CV changes
  useEffect(() => {
    if (currentCv?.id && activeTab === 'coverLetter') {
      loadCoverLetters();
    }
  }, [currentCv?.id, activeTab]);

  // Render List View
  const renderListView = () => (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">CV Builder V3</h1>
            <p className="text-gray-500">Modular templates with sidebar flexibility</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
            <Button onClick={() => setView('create')} data-testid="create-v3-cv-btn">
              <Plus className="w-4 h-4 mr-2" /> New CV
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search CVs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : cvList.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No V3 CVs yet</h3>
            <p className="text-gray-500 mb-4">Create your first CV with modular templates</p>
            <Button onClick={() => setView('create')}>
              <Plus className="w-4 h-4 mr-2" /> Create CV
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cvList.map((cv) => (
              <Card 
                key={cv.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/cv-builder-v3/${cv.id}`)}
                data-testid={`v3-cv-card-${cv.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{cv.candidateName}</h3>
                      <p className="text-sm text-gray-500">{cv.candidateTitle || 'No title'}</p>
                    </div>
                    <div 
                      className="w-6 h-6 rounded-full" 
                      style={{ backgroundColor: cv.sidebarColor || '#1e3a5f' }}
                    />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
                    <span>{cv.v3TemplateId}</span>
                    <span>{new Date(cv.updatedAt).toLocaleDateString()}</span>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); navigate(`/cv-builder-v3/${cv.id}`); }}
                    >
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); handleDelete(cv.id); }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render Create View
  const renderCreateView = () => (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => setView('list')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">Create V3 CV</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={candidateName}
                    onChange={(e) => setCandidateName(e.target.value)}
                    placeholder="John Doe"
                    data-testid="v3-candidate-name"
                  />
                </div>
                <div>
                  <Label>Language</Label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="w-full h-10 px-3 border rounded-md"
                  >
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="es">Spanish</option>
                  </select>
                </div>
                <div>
                  <Label>CV Content *</Label>
                  <Textarea
                    value={rawText}
                    onChange={(e) => setRawText(e.target.value)}
                    placeholder="Paste your CV content here (raw text, old CV, LinkedIn export, etc.)..."
                    rows={12}
                    data-testid="v3-raw-text"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Smart Create Card */}
            <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Zap className="w-4 h-4 text-purple-600" />
                  Smart Create
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium">Professional Rewrite</p>
                    <p className="text-xs text-gray-500">
                      AI will professionally rewrite and structure your CV content
                    </p>
                  </div>
                  <Switch
                    checked={atsOptimize}
                    onCheckedChange={setAtsOptimize}
                    data-testid="ats-optimize-toggle"
                  />
                </div>
                
                {atsOptimize && (
                  <div>
                    <Label className="text-sm">Target Role (Optional)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Target className="w-4 h-4 text-gray-400" />
                      <Input
                        value={targetRole}
                        onChange={(e) => setTargetRole(e.target.value)}
                        placeholder="e.g., Software Engineer, Product Manager"
                        className="flex-1"
                        data-testid="target-role-input"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Specify a role to tailor your CV for specific positions
                    </p>
                  </div>
                )}
                
                <div className="bg-white/60 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-medium text-purple-700">What Smart Create does:</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span>Rewrites content with professional language</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span>Uses strong action verbs (Led, Developed, Managed)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span>Organizes information with clear structure</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span>Writes a compelling professional summary</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Button 
              onClick={handleCreate} 
              disabled={generating}
              className={`w-full ${atsOptimize ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
              data-testid="v3-generate-btn"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {atsOptimize ? 'Creating Professional CV...' : 'Generating...'}</>
              ) : (
                <><Zap className="w-4 h-4 mr-2" /> {atsOptimize ? 'Create Professional CV' : 'Generate CV'}</>
              )}
            </Button>
          </div>

          {/* Right Panel - Template Selection */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Choose Template</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Sidebar Templates */}
                <div>
                  <Label className="text-xs text-gray-500 uppercase">Sidebar Templates</Label>
                  <div className="mt-2 space-y-2">
                    {templates.filter(t => t.category === 'sidebar').map((template) => (
                      <div
                        key={template.id}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedTemplate === template.id 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedTemplate(template.id)}
                        data-testid={`template-${template.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: template.previewColor }}
                          />
                          <span className="font-medium text-sm">{template.name}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{template.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Normal Templates */}
                <div>
                  <Label className="text-xs text-gray-500 uppercase">Professional Templates</Label>
                  <div className="mt-2 space-y-2">
                    {templates.filter(t => t.category === 'normal').map((template) => (
                      <div
                        key={template.id}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedTemplate === template.id 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedTemplate(template.id)}
                        data-testid={`template-${template.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: template.previewColor }}
                          />
                          <span className="font-medium text-sm">{template.name}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{template.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sidebar Color (only for sidebar templates) */}
                {templates.find(t => t.id === selectedTemplate)?.category === 'sidebar' && (
                  <div>
                    <Label className="text-xs text-gray-500 uppercase">Sidebar Color</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {SIDEBAR_COLORS.map((color) => (
                        <button
                          key={color.id}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            sidebarColor === color.value 
                              ? 'border-gray-800 scale-110' 
                              : 'border-transparent hover:scale-105'
                          }`}
                          style={{ backgroundColor: color.value }}
                          onClick={() => setSidebarColor(color.value)}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Edit View
  const renderEditView = () => {
    if (!structuredCv) return null;
    
    const currentTemplate = templates.find(t => t.id === selectedTemplate);
    const isSidebarTemplate = currentTemplate?.category === 'sidebar';

    return (
      <div className="min-h-screen bg-gray-100 flex">
        {/* Left Panel - Editor */}
        <div className="w-[420px] bg-white border-r border-gray-200 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => {
              setView('list');
              setCurrentCv(null);
              setStructuredCv(null);
              setActiveTab('cv');
              setCurrentCoverLetter(null);
              navigate('/cv-builder-v3');
            }}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={activeTab === 'cv' ? refreshPreview : () => {}}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              {activeTab === 'cv' ? (
                <Button size="sm" onClick={() => saveCV({})} disabled={saving} data-testid="save-v3-cv-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save
                </Button>
              ) : currentCoverLetter && (
                <Button size="sm" onClick={updateCoverLetter} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save
                </Button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 ${
                activeTab === 'cv' 
                  ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('cv')}
              data-testid="cv-tab"
            >
              <FileText className="w-4 h-4" /> CV
            </button>
            <button
              className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 ${
                activeTab === 'coverLetter' 
                  ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('coverLetter')}
              data-testid="cover-letter-tab"
            >
              <Mail className="w-4 h-4" /> Cover Letter
            </button>
          </div>

          {/* Editor Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {activeTab === 'cv' ? (
              <>
            {/* Template & Photo Settings */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="py-3">
                <CardTitle className="text-gray-900 text-sm flex items-center gap-2">
                  <Palette className="w-4 h-4" /> Template Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Template Selection */}
                <div>
                  <Label className="text-xs">Template</Label>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    className="w-full h-9 px-3 border rounded-md text-sm"
                  >
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                {/* Photo Toggle */}
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-2">
                    <User className="w-3 h-3" /> Show Photo
                  </Label>
                  <Switch
                    checked={showPhoto}
                    onCheckedChange={handlePhotoToggle}
                  />
                </div>

                {/* Sidebar Color (only for sidebar templates) */}
                {isSidebarTemplate && (
                  <div>
                    <Label className="text-xs">Sidebar Color</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {SIDEBAR_COLORS.map((color) => (
                        <button
                          key={color.id}
                          className={`w-7 h-7 rounded-full border-2 transition-all ${
                            sidebarColor === color.value 
                              ? 'border-gray-800 scale-110' 
                              : 'border-transparent hover:scale-105'
                          }`}
                          style={{ backgroundColor: color.value }}
                          onClick={() => handleSidebarColorChange(color.value)}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Smart Edit Panel */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="py-3 cursor-pointer" onClick={() => setShowSmartEdit(!showSmartEdit)}>
                <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-500" /> Smart Edit
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showSmartEdit ? 'rotate-180' : ''}`} />
                </CardTitle>
              </CardHeader>
              {showSmartEdit && (
                <CardContent className="space-y-3 pt-0">
                  <p className="text-xs text-gray-500">
                    Paste CV content and let AI extract and merge it with your current CV
                  </p>
                  <Textarea
                    value={smartEditContent}
                    onChange={(e) => setSmartEditContent(e.target.value)}
                    placeholder="Paste your experience, education, skills, or any CV content here..."
                    className="min-h-[120px] text-sm"
                    data-testid="smart-edit-textarea"
                  />
                  <div className="flex items-center gap-2">
                    <Select value={smartEditMode} onValueChange={setSmartEditMode}>
                      <SelectTrigger className="flex-1 h-8 text-xs" data-testid="smart-edit-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conservative">Conservative (Add only)</SelectItem>
                        <SelectItem value="full">Full Update</SelectItem>
                        <SelectItem value="replace">Replace All</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={handleSmartEdit}
                      disabled={smartEditLoading || !smartEditContent.trim()}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="smart-edit-btn"
                    >
                      {smartEditLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ClipboardPaste className="w-4 h-4 mr-1" />
                          Apply
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="text-xs text-gray-400 space-y-1">
                    <p><strong>Conservative:</strong> Only add new content, never overwrite</p>
                    <p><strong>Full Update:</strong> Update existing fields with new values</p>
                    <p><strong>Replace:</strong> Replace entire sections with parsed content</p>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Update History Panel */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="history-panel">
              <CardHeader className="py-3 cursor-pointer" onClick={() => setShowHistory(!showHistory)}>
                <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <History className="w-4 h-4 text-blue-500" /> Update History
                    {versions.length > 0 && (
                      <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">
                        {versions.length}
                      </span>
                    )}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                </CardTitle>
              </CardHeader>
              {showHistory && (
                <CardContent className="space-y-2 pt-0">
                  <p className="text-xs text-gray-500">
                    Every save creates a snapshot. Restore any previous version anytime.
                  </p>
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-4 text-gray-400 text-xs">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading history...
                    </div>
                  ) : versions.length === 0 ? (
                    <div className="text-center py-4 text-xs text-gray-400" data-testid="history-empty">
                      No history yet. Make a change to create your first snapshot.
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto space-y-1.5" data-testid="history-list">
                      {versions.map((v) => (
                        <div
                          key={v.id}
                          className="flex items-start justify-between gap-2 p-2 bg-gray-50 hover:bg-gray-100 rounded text-xs"
                          data-testid={`history-version-${v.versionNumber}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-gray-700">v{v.versionNumber}</span>
                              <span className="text-gray-400">•</span>
                              <span className="text-gray-500">
                                {new Date(v.createdAt).toLocaleString(undefined, {
                                  month: 'short', day: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <div className="text-gray-600 truncate mt-0.5" title={v.changeNote}>
                              {v.changeNote}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-blue-600 hover:bg-blue-50"
                              onClick={() => handleRestoreVersion(v.id, v.versionNumber)}
                              data-testid={`history-restore-${v.versionNumber}`}
                              title="Restore this version"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                            {versions.length > 1 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-red-500 hover:bg-red-50"
                                onClick={() => handleDeleteVersion(v.id, v.versionNumber)}
                                data-testid={`history-delete-${v.versionNumber}`}
                                title="Delete this version"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Section Layout (for sidebar templates) */}
            {isSidebarTemplate && (
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="py-3">
                  <CardTitle className="text-gray-900 text-sm flex items-center gap-2">
                    <ArrowLeftRight className="w-4 h-4" /> Section Layout
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {/* Sidebar */}
                    <div>
                      <Label className="text-xs text-gray-500">Sidebar</Label>
                      <div className="mt-1 space-y-1 min-h-[80px] p-2 bg-gray-50 rounded">
                        {sectionLayout.sidebar?.map((sectionId) => {
                          const section = MOVABLE_SECTIONS.find(s => s.id === sectionId);
                          return section ? (
                            <div key={sectionId} className="flex items-center justify-between p-1.5 bg-white rounded text-xs">
                              <span>{section.label}</span>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-5 w-5 p-0"
                                onClick={() => moveSection(sectionId, 'main')}
                              >
                                <ChevronDown className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                    {/* Main */}
                    <div>
                      <Label className="text-xs text-gray-500">Main</Label>
                      <div className="mt-1 space-y-1 min-h-[80px] p-2 bg-gray-50 rounded">
                        {sectionLayout.main?.filter(s => MOVABLE_SECTIONS.some(ms => ms.id === s)).map((sectionId) => {
                          const section = MOVABLE_SECTIONS.find(s => s.id === sectionId);
                          return section ? (
                            <div key={sectionId} className="flex items-center justify-between p-1.5 bg-white rounded text-xs">
                              <span>{section.label}</span>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-5 w-5 p-0"
                                onClick={() => moveSection(sectionId, 'sidebar')}
                              >
                                <ChevronUp className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Photo Upload */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="py-3">
                <CardTitle className="text-gray-900 text-sm flex items-center gap-2">
                  <Camera className="w-4 h-4" /> Profile Photo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  {currentCv?.photoUrl ? (
                    <img src={currentCv.photoUrl} alt="Profile" className="w-16 h-16 rounded-full object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                      <User className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                    <Button variant="outline" size="sm" disabled={photoUploading} asChild>
                      <span>
                        {photoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                        Upload
                      </span>
                    </Button>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Personal Info */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="py-3">
                <CardTitle className="text-gray-900 text-sm">Personal Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  value={structuredCv.candidate?.name || ''}
                  onChange={(e) => updateField('candidate.name', e.target.value)}
                  placeholder="Full Name"
                  className="bg-gray-50"
                />
                <Input
                  value={structuredCv.candidate?.title || ''}
                  onChange={(e) => updateField('candidate.title', e.target.value)}
                  placeholder="Job Title"
                  className="bg-gray-50"
                />
                <Textarea
                  value={structuredCv.candidate?.summary || ''}
                  onChange={(e) => updateField('candidate.summary', e.target.value)}
                  placeholder="Professional Summary"
                  rows={3}
                  className="bg-gray-50"
                />
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="py-3">
                <CardTitle className="text-gray-900 text-sm">Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  value={structuredCv.contact?.email || ''}
                  onChange={(e) => updateField('contact.email', e.target.value)}
                  placeholder="Email"
                  className="bg-gray-50"
                />
                <Input
                  value={structuredCv.contact?.phone || ''}
                  onChange={(e) => updateField('contact.phone', e.target.value)}
                  placeholder="Phone"
                  className="bg-gray-50"
                />
                <Input
                  value={structuredCv.contact?.location || structuredCv.contact?.address || ''}
                  onChange={(e) => {
                    updateField('contact.location', e.target.value);
                    updateField('contact.address', e.target.value);
                  }}
                  placeholder="Location"
                  className="bg-gray-50"
                />
              </CardContent>
            </Card>

            {/* Experience */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="py-3">
                <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                  Experience
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={sectionVisibility.experience !== false}
                      onCheckedChange={() => toggleSectionVisibility('experience')}
                    />
                    <Button size="sm" variant="ghost" onClick={addExperience}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {structuredCv.experience?.map((exp, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg space-y-2 border border-gray-100">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex items-center gap-1">
                        <div className="flex flex-col">
                          <Button size="sm" variant="ghost" onClick={() => moveExperienceUp(idx)} disabled={idx === 0} className="h-5 w-5 p-0">
                            <ChevronUp className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => moveExperienceDown(idx)} disabled={idx >= (structuredCv.experience?.length || 0) - 1} className="h-5 w-5 p-0">
                            <ChevronDown className="w-3 h-3" />
                          </Button>
                        </div>
                        <span className="text-xs text-gray-400">#{idx + 1}</span>
                      </div>
                      <Input
                        value={exp.role || ''}
                        onChange={(e) => updateField(`experience[${idx}].role`, e.target.value)}
                        placeholder="Job Title"
                        className="bg-white flex-1"
                      />
                      <Button size="sm" variant="ghost" onClick={() => removeExperience(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <Input
                      value={exp.company || ''}
                      onChange={(e) => updateField(`experience[${idx}].company`, e.target.value)}
                      placeholder="Company"
                      className="bg-white"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={exp.start || ''}
                        onChange={(e) => updateField(`experience[${idx}].start`, e.target.value)}
                        placeholder="Start"
                        className="bg-white"
                      />
                      <Input
                        value={exp.end || ''}
                        onChange={(e) => updateField(`experience[${idx}].end`, e.target.value)}
                        placeholder="End"
                        className="bg-white"
                      />
                    </div>
                    {/* Bullets */}
                    <div className="mt-2 pt-2 border-t">
                      <div className="flex justify-between items-center mb-2">
                        <Label className="text-xs text-gray-600">Tasks</Label>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => generateTasks(idx)}
                            disabled={generatingTasksFor === idx}
                            className="h-6 px-2 text-purple-600 border-purple-200"
                          >
                            {generatingTasksFor === idx ? (
                              <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />...</>
                            ) : (
                              <><Wand2 className="w-3 h-3 mr-1" /> AI</>
                            )}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => addBullet(idx)} className="h-6 px-2">
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      {(exp.bullets || []).map((bullet, bIdx) => (
                        <div key={bIdx} className="flex gap-2 mb-1">
                          <span className="text-gray-400 mt-2">•</span>
                          <Input
                            value={bullet}
                            onChange={(e) => updateBullet(idx, bIdx, e.target.value)}
                            className="bg-white flex-1 h-8 text-sm"
                          />
                          <Button size="sm" variant="ghost" onClick={() => removeBullet(idx, bIdx)} className="h-8 px-1">
                            <X className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Education */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="py-3">
                <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                  Education
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={sectionVisibility.education !== false}
                      onCheckedChange={() => toggleSectionVisibility('education')}
                    />
                    <Button size="sm" variant="ghost" onClick={addEducation}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {structuredCv.education?.map((edu, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg space-y-2 border border-gray-100">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex items-center gap-1">
                        <div className="flex flex-col">
                          <Button size="sm" variant="ghost" onClick={() => moveEducationUp(idx)} disabled={idx === 0} className="h-5 w-5 p-0">
                            <ChevronUp className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => moveEducationDown(idx)} disabled={idx >= (structuredCv.education?.length || 0) - 1} className="h-5 w-5 p-0">
                            <ChevronDown className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <Input
                        value={edu.degree || ''}
                        onChange={(e) => updateField(`education[${idx}].degree`, e.target.value)}
                        placeholder="Degree"
                        className="bg-white flex-1"
                      />
                      <Button size="sm" variant="ghost" onClick={() => removeEducation(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <Input
                      value={edu.school || ''}
                      onChange={(e) => updateField(`education[${idx}].school`, e.target.value)}
                      placeholder="School"
                      className="bg-white"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={edu.start || ''}
                        onChange={(e) => updateField(`education[${idx}].start`, e.target.value)}
                        placeholder="Start"
                        className="bg-white"
                      />
                      <Input
                        value={edu.end || ''}
                        onChange={(e) => updateField(`education[${idx}].end`, e.target.value)}
                        placeholder="End"
                        className="bg-white"
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Skills */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="py-3">
                <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                  Skills
                  <Switch
                    checked={sectionVisibility.skills !== false}
                    onCheckedChange={() => toggleSectionVisibility('skills')}
                  />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={(structuredCv.skills?.all || []).join(', ')}
                  onChange={(e) => updateField('skills.all', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  placeholder="Python, JavaScript, React..."
                  rows={2}
                  className="bg-gray-50"
                />
              </CardContent>
            </Card>

            {/* Languages */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="py-3">
                <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                  Languages
                  <Switch
                    checked={sectionVisibility.languages !== false}
                    onCheckedChange={() => toggleSectionVisibility('languages')}
                  />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={(structuredCv.languages || []).join(', ')}
                  onChange={(e) => updateField('languages', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  placeholder="English (Native), Spanish (Fluent)..."
                  rows={2}
                  className="bg-gray-50"
                />
              </CardContent>
            </Card>

            {/* Certifications */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="py-3">
                <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-amber-600" />
                    Certifications
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={sectionVisibility.certifications !== false}
                      onCheckedChange={() => toggleSectionVisibility('certifications')}
                    />
                    <Button size="sm" variant="ghost" onClick={addCertification}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(structuredCv.certifications || []).map((cert, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg space-y-2 border border-gray-100">
                    <div className="flex justify-between items-start gap-2">
                      <Input
                        value={cert.name || ''}
                        onChange={(e) => updateField(`certifications[${idx}].name`, e.target.value)}
                        placeholder="Certification Name"
                        className="bg-white flex-1"
                      />
                      <Button size="sm" variant="ghost" onClick={() => removeCertification(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={cert.issuer || ''}
                        onChange={(e) => updateField(`certifications[${idx}].issuer`, e.target.value)}
                        placeholder="Issuer"
                        className="bg-white"
                      />
                      <Input
                        value={cert.year || ''}
                        onChange={(e) => updateField(`certifications[${idx}].year`, e.target.value)}
                        placeholder="Year"
                        className="bg-white"
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Custom Sections */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="py-3">
                <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <LayoutList className="w-4 h-4 text-indigo-600" />
                    Custom Sections
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={sectionVisibility.customSections !== false}
                      onCheckedChange={() => toggleSectionVisibility('customSections')}
                    />
                    <Button size="sm" variant="ghost" onClick={addCustomSection} data-testid="add-custom-section-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(structuredCv.customSections || []).length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-2">
                    Add custom sections like Volunteer Work, Publications, Hobbies, etc.
                  </p>
                )}
                {(structuredCv.customSections || []).map((section, sIdx) => (
                  <div key={sIdx} className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
                    <div className="flex justify-between items-center gap-2 mb-3">
                      <Input
                        value={section.title || ''}
                        onChange={(e) => updateCustomSectionTitle(sIdx, e.target.value)}
                        placeholder="Section Title"
                        className="bg-white font-medium flex-1"
                        data-testid={`custom-section-title-${sIdx}`}
                      />
                      <Button size="sm" variant="ghost" onClick={() => removeCustomSection(sIdx)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(section.items || []).map((item, iIdx) => (
                        <div key={iIdx} className="flex gap-2">
                          <span className="text-indigo-400 mt-2">•</span>
                          <Input
                            value={item}
                            onChange={(e) => updateCustomSectionItem(sIdx, iIdx, e.target.value)}
                            placeholder="Enter item..."
                            className="bg-white flex-1 h-8 text-sm"
                            data-testid={`custom-section-${sIdx}-item-${iIdx}`}
                          />
                          <Button size="sm" variant="ghost" onClick={() => removeCustomSectionItem(sIdx, iIdx)} className="h-8 px-1">
                            <X className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      ))}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => addCustomSectionItem(sIdx)}
                        className="w-full h-7 text-xs border-dashed border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Item
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
              </>
            ) : (
              /* Cover Letter Tab Content */
              <>
                {/* Cover Letter Type Selection */}
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader className="py-3">
                    <CardTitle className="text-gray-900 text-sm flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Generate Cover Letter
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Letter Type */}
                    <div>
                      <Label className="text-xs">Letter Type</Label>
                      <div className="mt-2 flex gap-2">
                        <button
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                            coverLetterType === 'general'
                              ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500'
                              : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:border-gray-300'
                          }`}
                          onClick={() => setCoverLetterType('general')}
                        >
                          General
                        </button>
                        <button
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                            coverLetterType === 'specific'
                              ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500'
                              : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:border-gray-300'
                          }`}
                          onClick={() => setCoverLetterType('specific')}
                        >
                          <Briefcase className="w-3 h-3 inline mr-1" />
                          Specific Job
                        </button>
                      </div>
                    </div>

                    {/* Specific Job Fields */}
                    {coverLetterType === 'specific' && (
                      <div className="space-y-3 pt-2 border-t border-gray-100">
                        <div>
                          <Label className="text-xs">Job Title *</Label>
                          <Input
                            value={coverLetterJobTitle}
                            onChange={(e) => setCoverLetterJobTitle(e.target.value)}
                            placeholder="Software Engineer"
                            className="bg-gray-50"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Company Name</Label>
                          <Input
                            value={coverLetterCompany}
                            onChange={(e) => setCoverLetterCompany(e.target.value)}
                            placeholder="Google"
                            className="bg-gray-50"
                          />
                        </div>
                      </div>
                    )}

                    {/* Hiring Manager */}
                    <div>
                      <Label className="text-xs">Hiring Manager</Label>
                      <Input
                        value={coverLetterHiringManager}
                        onChange={(e) => setCoverLetterHiringManager(e.target.value)}
                        placeholder="Hiring Manager"
                        className="bg-gray-50"
                      />
                    </div>

                    {/* Generate Button */}
                    <Button 
                      className="w-full"
                      onClick={generateCoverLetter}
                      disabled={generatingCoverLetter}
                    >
                      {generatingCoverLetter ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Wand2 className="w-4 h-4 mr-2" /> Generate Cover Letter</>
                      )}
                    </Button>
                  </CardContent>
                </Card>

                {/* Previous Cover Letters */}
                {coverLetters.length > 0 && (
                  <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader className="py-3">
                      <CardTitle className="text-gray-900 text-sm">Previous Letters</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {coverLetters.map((letter) => (
                        <div 
                          key={letter.id}
                          className={`p-2 rounded-lg cursor-pointer flex items-center justify-between ${
                            currentCoverLetter?.id === letter.id
                              ? 'bg-indigo-50 border border-indigo-200'
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                          onClick={() => selectCoverLetter(letter)}
                        >
                          <div>
                            <p className="text-sm font-medium">
                              {letter.letterType === 'specific' 
                                ? `${letter.jobTitle}${letter.companyName ? ` @ ${letter.companyName}` : ''}`
                                : 'General Cover Letter'
                              }
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(letter.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); deleteCoverLetter(letter.id); }}
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Edit Cover Letter Content */}
                {currentCoverLetter && (
                  <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader className="py-3">
                      <CardTitle className="text-gray-900 text-sm flex items-center gap-2">
                        <Edit3 className="w-4 h-4" /> Edit Content
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        value={coverLetterContent}
                        onChange={(e) => setCoverLetterContent(e.target.value)}
                        placeholder="Cover letter content..."
                        rows={12}
                        className="bg-gray-50 text-sm"
                      />
                      <p className="text-xs text-gray-400 mt-2">
                        Tip: Use double line breaks to separate paragraphs
                      </p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>

          {/* Download Buttons */}
          <div className="p-4 border-t border-gray-200 space-y-2">
            {activeTab === 'cv' ? (
              <>
                <Button 
                  className="w-full" 
                  onClick={() => window.open(v3API.exportPDFClean(currentCv?.id), '_blank')}
                  disabled={!currentCv?.id}
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.open(v3API.exportPDFWatermark(currentCv?.id), '_blank')}
                  disabled={!currentCv?.id}
                >
                  <Download className="w-4 h-4 mr-2" /> Download (Watermark)
                </Button>
              </>
            ) : currentCoverLetter && (
              <div className="space-y-2">
                <Button 
                  className="w-full" 
                  onClick={() => window.open(v3API.exportCoverLetterPDF(currentCoverLetter?.id), '_blank')}
                  disabled={!currentCoverLetter?.id}
                >
                  <Download className="w-4 h-4 mr-2" /> Download Cover Letter PDF
                </Button>
                <Button 
                  variant="outline"
                  className="w-full" 
                  onClick={() => window.open(v3API.exportCoverLetterPDFWatermark(currentCoverLetter?.id), '_blank')}
                  disabled={!currentCoverLetter?.id}
                >
                  <Download className="w-4 h-4 mr-2" /> Download (Watermark)
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="flex-1 bg-gray-200 p-6 overflow-auto">
          <div className="bg-white shadow-lg mx-auto" style={{ width: '210mm', minHeight: '297mm' }}>
            {activeTab === 'cv' ? (
              previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  className="w-full h-full border-0"
                  style={{ minHeight: '297mm' }}
                  title="CV Preview"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              )
            ) : (
              coverLetterPreviewHtml ? (
                <iframe
                  srcDoc={coverLetterPreviewHtml}
                  className="w-full h-full border-0"
                  style={{ minHeight: '297mm' }}
                  title="Cover Letter Preview"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 flex-col gap-4">
                  <Mail className="w-12 h-12" />
                  <p className="text-sm">Generate a cover letter to see preview</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  // Main render
  if (loading && !structuredCv) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (view === 'list') return renderListView();
  if (view === 'create') return renderCreateView();
  if (view === 'edit') return renderEditView();

  return null;
}
